/* Perfect Pass — Free Shipping Progress Bar
   Reads config from <script type="application/json" class="pp-pb-config"> inside .pp-pb-root.
   Mounts a bar into every <div class="pb-mount" data-pos="header|cart|cart-page" ...> on the page.
   Single fetch to /cart.js per real cart event — no defensive polling.
*/

(function () {
  'use strict';

  const ROOT_SELECTOR = '.pp-pb-root';
  const CONFIG_SELECTOR = '.pp-pb-root .pp-pb-config';
  const MOUNT_SELECTOR = '.pb-mount';
  const STORAGE_KEY = 'pp_pb_state_' + (window.Shopify && window.Shopify.shop ? window.Shopify.shop : 'default');
  const STORAGE_TTL_MS = 30 * 60 * 1000;
  const FETCH_DEBOUNCE_MS = 250;
  const FETCH_TIMEOUT_MS = 5000;

  const state = {
    config: null,
    cartTotal: 0,
    cartKnown: false,
    instances: new Map(),
    fetchInProgress: false,
    fetchDebounce: null,
    lastUnlocked: false,
  };

  // ---- Config & cache ------------------------------------------------------

  function readConfig() {
    const el = document.querySelector(CONFIG_SELECTOR);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function loadCachedTotal() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.timestamp > STORAGE_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return typeof parsed.total === 'number' ? parsed.total : null;
    } catch (e) {
      return null;
    }
  }

  function saveCachedTotal(total) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ total: total, timestamp: Date.now() }));
    } catch (e) {}
  }

  // ---- Pure helpers --------------------------------------------------------

  function calculateProgress(cartTotal) {
    const cfg = state.config;
    if (!cfg || !cfg.freeShippingAmount || cfg.freeShippingAmount <= 0 || !cfg.freeShippingLabel) {
      return { pct: 0, amountLeftCents: 0, configured: false, unlocked: false };
    }
    const total = Math.max(0, Number(cartTotal) || 0);
    const pct = Math.min(100, (total / cfg.freeShippingAmount) * 100);
    const amountLeftCents = Math.max(0, cfg.freeShippingAmount - total);
    return {
      pct: pct,
      amountLeftCents: amountLeftCents,
      configured: true,
      unlocked: amountLeftCents <= 0,
    };
  }

  function formatCurrency(cents) {
    const cfg = state.config;
    const amt = (cents / 100).toFixed(2);
    return cfg.currencyAfter ? amt + cfg.currency : cfg.currency + amt;
  }

  function buildText(progress) {
    const cfg = state.config;
    if (!progress.configured) return 'No free shipping configured';
    if (progress.unlocked) {
      const custom = (cfg.textCongratulations || '').trim();
      return custom || ("Congratulations! You've reached " + cfg.freeShippingLabel + '!');
    }
    return cfg.textSpend + ' ' + formatCurrency(progress.amountLeftCents) + ' ' + cfg.textToReach + ' ' + cfg.freeShippingLabel + '!';
  }

  // ---- Mount lifecycle -----------------------------------------------------

  function applyMountStyle(mount) {
    const cfg = state.config;
    const padTop = mount.getAttribute('data-padding-top') || '0px';
    const padBottom = mount.getAttribute('data-padding-bottom') || '0px';
    const width = mount.getAttribute('data-width') || '100%';

    mount.style.setProperty('--pb-width', width);
    mount.style.setProperty('--pb-padding-top', padTop);
    mount.style.setProperty('--pb-padding-bottom', padBottom);
    mount.style.setProperty('--pb-progress-color', cfg.progressColor);
    mount.style.setProperty('--pb-bg-color', cfg.bgColor);
    mount.style.setProperty('--pb-wrap-bg-color', cfg.wrapBgColor || 'transparent');
    mount.style.setProperty('--pb-text-color', cfg.textColor);
    mount.style.setProperty('--pb-bar-height', cfg.barHeight + 'px');
    mount.style.setProperty('--pb-font-size', cfg.fontSize + 'px');
    mount.style.setProperty('--pb-font-weight', cfg.bold ? '600' : 'normal');
    mount.style.setProperty('--pb-radius', cfg.rounded ? '25px' : '0');
  }

  function buildInnerHTML() {
    const stripeAttr = state.config.striped ? ' data-stripes="1"' : '';
    return (
      '<div class="pb-wrap">' +
        '<p class="pb-text" aria-live="polite"></p>' +
        '<div class="pb-container" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-label="Free shipping progress"' + stripeAttr + '>' +
          '<div class="pb-bar"></div>' +
        '</div>' +
        '<p class="pb-nudge" hidden></p>' +
        '<a class="pb-cross-sell" hidden></a>' +
      '</div>'
    );
  }

  function mountBar(mount) {
    if (!mount || mount.getAttribute('data-pb-mounted') === '1' || !state.config) return;

    applyMountStyle(mount);
    mount.innerHTML = buildInnerHTML();
    mount.setAttribute('data-pb-mounted', '1');
    mount.setAttribute('data-state', state.cartKnown ? 'progress' : 'skeleton');

    const instance = {
      element: mount,
      barEl: mount.querySelector('.pb-bar'),
      textEl: mount.querySelector('.pb-text'),
      containerEl: mount.querySelector('.pb-container'),
      nudgeEl: mount.querySelector('.pb-nudge'),
      crossSellEl: mount.querySelector('.pb-cross-sell'),
      lastPct: -1,
      lastText: '',
    };
    state.instances.set(mount, instance);

    renderInstance(instance);
  }

  // ---- Render --------------------------------------------------------------

  function renderInstance(instance) {
    const cfg = state.config;
    const progress = calculateProgress(state.cartTotal);
    const text = buildText(progress);

    if (instance.lastText !== text) {
      instance.textEl.textContent = text;
      instance.lastText = text;
    }

    if (Math.abs(instance.lastPct - progress.pct) > 0.1) {
      instance.barEl.style.width = progress.pct + '%';
      instance.containerEl.setAttribute('aria-valuenow', String(Math.round(progress.pct)));
      instance.lastPct = progress.pct;
    }

    let nextState;
    if (!state.cartKnown) {
      nextState = 'skeleton';
    } else if (progress.unlocked) {
      nextState = 'unlocked';
    } else {
      nextState = 'progress';
    }
    if (instance.element.getAttribute('data-state') !== nextState) {
      instance.element.setAttribute('data-state', nextState);
    }

    // Near-threshold nudge: 80%+ (configurable) but not yet unlocked
    const nudgeThreshold = Number(cfg.nearThresholdNudgePct) || 80;
    if (progress.configured && progress.pct >= nudgeThreshold && !progress.unlocked) {
      instance.nudgeEl.textContent = "You're almost there!";
      instance.nudgeEl.hidden = false;
    } else if (!instance.nudgeEl.hidden) {
      instance.nudgeEl.hidden = true;
    }

    // Cross-sell hint: 60–95% range, only if collection URL configured
    if (cfg.crossSellUrl && progress.configured && progress.pct >= 60 && progress.pct < 95) {
      instance.crossSellEl.textContent = cfg.crossSellLabel || 'Browse add-ons';
      instance.crossSellEl.href = cfg.crossSellUrl;
      instance.crossSellEl.hidden = false;
    } else if (!instance.crossSellEl.hidden) {
      instance.crossSellEl.hidden = true;
    }
  }

  function renderAll() {
    state.instances.forEach(renderInstance);
  }

  function setCartTotal(newTotal) {
    const wasUnlocked = state.lastUnlocked;
    state.cartTotal = newTotal;
    state.cartKnown = true;
    saveCachedTotal(newTotal);

    const progress = calculateProgress(newTotal);
    state.lastUnlocked = progress.unlocked;

    renderAll();

    // Replay shimmer animation when threshold is newly crossed.
    if (state.config.showCelebration && !wasUnlocked && progress.unlocked) {
      state.instances.forEach(function (inst) {
        inst.element.setAttribute('data-state', 'progress');
        // Force reflow to restart the [data-state="unlocked"]::after animation.
        void inst.element.offsetWidth;
        inst.element.setAttribute('data-state', 'unlocked');
      });
    }
  }

  // ---- Cart fetching -------------------------------------------------------

  async function fetchCart() {
    if (state.fetchInProgress) return;
    state.fetchInProgress = true;
    try {
      const ctrl = new AbortController();
      const timeoutId = setTimeout(function () { ctrl.abort(); }, FETCH_TIMEOUT_MS);
      const res = await fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: ctrl.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) return;
      const data = await res.json();
      if (typeof data.total_price === 'number') {
        setCartTotal(data.total_price);
      }
    } catch (e) {
      if (window.Shopify && window.Shopify.designMode) {
        console.warn('[pp-progress-bar] cart fetch failed:', e.message);
      }
    } finally {
      state.fetchInProgress = false;
    }
  }

  function debouncedFetchCart() {
    clearTimeout(state.fetchDebounce);
    state.fetchDebounce = setTimeout(fetchCart, FETCH_DEBOUNCE_MS);
  }

  // ---- Event wiring --------------------------------------------------------

  function attachCartEventListeners() {
    const events = [
      'cart:updated', 'cart:add', 'cart:remove', 'cart:change', 'cart:refresh',
      'ajaxCart:added', 'ajaxCart:updated', 'ajaxCart:removed',
      'cart-drawer:updated', 'cart-drawer:open', 'drawer:open',
      'product-form:add-to-cart',
    ];
    events.forEach(function (name) {
      document.addEventListener(name, debouncedFetchCart);
    });

    // Catch form submits to /cart endpoints (covers themes that don't dispatch cart events).
    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (form && form.action && /\/cart\b/.test(form.action)) {
        debouncedFetchCart();
      }
    }, true);
  }

  /**
   * Monkey-patch window.fetch so we detect ANY call to a cart-mutation endpoint
   * regardless of which theme code or app triggered it. Stock Dawn updates the cart
   * via fetch('/cart/change.js') without dispatching custom events, so without this
   * patch our bar would miss quantity changes and removals.
   */
  function patchFetch() {
    if (typeof window.fetch !== 'function' || window.__ppPbFetchPatched) return;
    window.__ppPbFetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    const cartMutationRe = /\/cart\/(add|change|update|clear)(\.js)?\b/i;

    window.fetch = function (input, init) {
      let url = '';
      if (typeof input === 'string') {
        url = input;
      } else if (input && typeof input.url === 'string') {
        url = input.url;
      }
      const isCartMutation = cartMutationRe.test(url);
      const promise = originalFetch(input, init);
      if (isCartMutation) {
        promise.then(function () { debouncedFetchCart(); }).catch(function () {});
      }
      return promise;
    };
  }

  function watchForNewMounts() {
    const observer = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const added = mutations[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          const node = added[j];
          if (!(node instanceof Element)) continue;
          if (node.matches && node.matches(MOUNT_SELECTOR)) {
            mountBar(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll(MOUNT_SELECTOR).forEach(mountBar);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function attachCustomizerHooks() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    function reloadConfigAndRender() {
      const cfg = readConfig();
      if (!cfg) return;
      state.config = cfg;
      state.instances.forEach(function (inst, mount) {
        applyMountStyle(mount);
        // Re-toggle stripes attribute on the inner container when settings change.
        if (inst.containerEl) {
          if (cfg.striped) {
            inst.containerEl.setAttribute('data-stripes', '1');
          } else {
            inst.containerEl.removeAttribute('data-stripes');
          }
        }
        renderInstance(inst);
      });
    }

    document.addEventListener('shopify:section:load', reloadConfigAndRender);
    document.addEventListener('shopify:section:select', reloadConfigAndRender);
    document.addEventListener('shopify:section:unload', function () {
      // Tear down on section removal so the customizer doesn't leave dead bars.
      state.instances.forEach(function (inst) {
        inst.element.removeAttribute('data-pb-mounted');
        inst.element.innerHTML = '';
      });
      state.instances.clear();
    });
  }

  // ---- Boot ----------------------------------------------------------------

  function init() {
    state.config = readConfig();
    if (!state.config) return;

    const cached = loadCachedTotal();
    if (cached !== null) {
      state.cartTotal = cached;
      state.cartKnown = true;
      const progress = calculateProgress(cached);
      state.lastUnlocked = progress.unlocked;
    }

    document.querySelectorAll(MOUNT_SELECTOR).forEach(mountBar);
    attachCartEventListeners();
    patchFetch();
    watchForNewMounts();
    attachCustomizerHooks();

    // Always confirm with a fresh cart total.
    fetchCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
